import React from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
    Grid,
} from '@mui/material';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import type {
    DenniStatistika,
    TydenneStatistika,
    PorovnaniReakce,
    HistogramDobyReakce,
    AktivitaDen,
    AktivitaHodina,
    TrendUcasti,
    KumulativniOdpovedi,
} from '../api/JsdhNotifikaceService/generated/models/OsobniStatistika';

export type UserChartView = 'trend' | 'tydenne' | 'reakce' | 'odpovedi' | 'histogram' | 'dny' | 'hodiny' | 'ucast' | 'kumulativni';

const ALL_USER_VIEWS: UserChartView[] = ['trend', 'tydenne', 'histogram', 'dny', 'hodiny', 'reakce', 'ucast', 'kumulativni', 'odpovedi'];

interface UserTrendChartProps {
    historieDenne?: DenniStatistika[];
    historieTydenne?: TydenneStatistika[];
    porovnaniReakce?: PorovnaniReakce[];
    histogramReakce?: HistogramDobyReakce[];
    aktivitaPodleDne?: AktivitaDen[];
    aktivitaPodleHodiny?: AktivitaHodina[];
    trendUcasti?: TrendUcasti[];
    kumulativniOdpovedi?: KumulativniOdpovedi[];
    celkemJedu: number;
    celkemNejedu: number;
    allowedViews?: UserChartView[];
}

type ChartView = UserChartView;

const UserTrendChart: React.FC<UserTrendChartProps> = ({
    historieDenne,
    historieTydenne,
    porovnaniReakce,
    histogramReakce,
    aktivitaPodleDne,
    aktivitaPodleHodiny,
    trendUcasti,
    kumulativniOdpovedi,
    celkemJedu,
    celkemNejedu,
    allowedViews,
}) => {
    const theme = useTheme();
    const effectiveViews = allowedViews ?? ALL_USER_VIEWS;
    const [view, setView] = React.useState<ChartView>(effectiveViews[0] ?? 'trend');

    const handleViewChange = (
        _event: React.MouseEvent<HTMLElement>,
        newView: ChartView | null
    ) => {
        if (newView) {
            setView(newView);
        }
    };

    // Transform daily data for chart
    const dailyData = React.useMemo(() => {
        if (!historieDenne || historieDenne.length === 0) return [];
        return historieDenne.map((d) => ({
            datum: formatDateLabel(d.datum),
            pocetPoplachu: d.pocetPoplachu,
            pocetJedu: d.pocetJedu,
            pocetNejedu: d.pocetNejedu,
        }));
    }, [historieDenne]);

    // Transform weekly data for chart
    const weeklyData = React.useMemo(() => {
        if (!historieTydenne || historieTydenne.length === 0) return [];
        return historieTydenne.map((w) => ({
            datum: formatDateLabel(w.datum),
            pocetPoplachu: w.pocetPoplachu,
            pocetJedu: w.pocetJedu,
            pocetNejedu: w.pocetNejedu,
        }));
    }, [historieTydenne]);

    // Transform reaction time comparison data
    const reactionData = React.useMemo(() => {
        if (!porovnaniReakce || porovnaniReakce.length === 0) return [];
        return porovnaniReakce
            .filter((r) => r.dobaReakceUzivatel !== null || r.dobaReakceJednotka !== null)
            .map((r) => ({
                datum: formatDateLabel(r.datum),
                uzivatel: r.dobaReakceUzivatel ? Math.round(r.dobaReakceUzivatel) : null,
                jednotka: r.dobaReakceJednotka ? Math.round(r.dobaReakceJednotka) : null,
            }));
    }, [porovnaniReakce]);

    // Histogram data
    const histogramData = React.useMemo(() => {
        if (!histogramReakce || histogramReakce.length === 0) return [];
        return histogramReakce.map((h) => ({
            bucket: h.bucket,
            pocet: h.pocet,
        }));
    }, [histogramReakce]);

    // Day of week data
    const dayOfWeekData = React.useMemo(() => {
        if (!aktivitaPodleDne || aktivitaPodleDne.length === 0) return [];
        return aktivitaPodleDne.map((d) => ({
            den: d.den,
            pocetOdpovedi: d.pocetOdpovedi,
            pocetJedu: d.pocetJedu,
            pocetNejedu: d.pocetNejedu,
            prumernaDobaReakce: d.prumernaDobaReakce ? Math.round(d.prumernaDobaReakce) : null,
        }));
    }, [aktivitaPodleDne]);

    // Hour of day data
    const hourOfDayData = React.useMemo(() => {
        if (!aktivitaPodleHodiny || aktivitaPodleHodiny.length === 0) return [];
        return aktivitaPodleHodiny.map((h) => ({
            hodina: `${h.hodina}:00`,
            pocetOdpovedi: h.pocetOdpovedi,
            pocetJedu: h.pocetJedu,
            pocetNejedu: h.pocetNejedu,
            prumernaDobaReakce: h.prumernaDobaReakce ? Math.round(h.prumernaDobaReakce) : null,
        }));
    }, [aktivitaPodleHodiny]);

    // Participation trend data
    const participationData = React.useMemo(() => {
        if (!trendUcasti || trendUcasti.length === 0) return [];
        return trendUcasti.map((t) => ({
            datum: formatDateLabel(t.datum),
            procentoUcasti: t.procentoUcasti,
            celkemPoplachu: t.celkemPoplachu,
            zodpovezenychPoplachu: t.zodpovezenychPoplachu,
        }));
    }, [trendUcasti]);

    // Cumulative data
    const cumulativeData = React.useMemo(() => {
        if (!kumulativniOdpovedi || kumulativniOdpovedi.length === 0) return [];
        // Take only every Nth point if too many data points
        const data = kumulativniOdpovedi;
        const step = data.length > 60 ? Math.ceil(data.length / 60) : 1;
        return data.filter((_, i) => i % step === 0 || i === data.length - 1).map((k) => ({
            datum: formatDateLabel(k.datum),
            celkem: k.kumulativniOdpovedi,
            jedu: k.kumulativniJedu,
            nejedu: k.kumulativniNejedu,
        }));
    }, [kumulativniOdpovedi]);

    // Pie chart data
    const odpovediData = React.useMemo(() => {
        return [
            { name: 'JEDU', value: celkemJedu, color: theme.palette.success.main },
            { name: 'NEJEDU', value: celkemNejedu, color: theme.palette.error.main },
        ];
    }, [celkemJedu, celkemNejedu, theme]);

    const noData =
        (!historieDenne || historieDenne.length === 0) &&
        (!historieTydenne || historieTydenne.length === 0) &&
        (!porovnaniReakce || porovnaniReakce.length === 0) &&
        (!histogramReakce || histogramReakce.length === 0) &&
        (!aktivitaPodleDne || aktivitaPodleDne.length === 0) &&
        (!aktivitaPodleHodiny || aktivitaPodleHodiny.length === 0) &&
        (!trendUcasti || trendUcasti.length === 0) &&
        (!kumulativniOdpovedi || kumulativniOdpovedi.length === 0);

    const chartTitles: Record<ChartView, string> = {
        trend: 'Denní trend (30 dní)',
        tydenne: 'Týdenní přehled (12 týdnů)',
        reakce: 'Porovnání doby reakce',
        odpovedi: 'Odpovědi celkem',
        histogram: 'Rozložení doby reakce',
        dny: 'Aktivita podle dne',
        hodiny: 'Aktivita podle hodiny',
        ucast: 'Trend účasti',
        kumulativni: 'Kumulativní odpovědi',
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6">
                    {chartTitles[view]}
                </Typography>

                <Grid container spacing={0.5} justifyContent="flex-end" sx={{ maxWidth: 600 }}>
                    {(effectiveViews.includes('trend') || effectiveViews.includes('tydenne') || effectiveViews.includes('histogram')) && (
                        <Grid>
                            <ToggleButtonGroup
                                value={view}
                                exclusive
                                onChange={handleViewChange}
                                size="small"
                            >
                                {effectiveViews.includes('trend') && (
                                    <ToggleButton value="trend" disabled={!historieDenne || historieDenne.length === 0}>
                                        Dny
                                    </ToggleButton>
                                )}
                                {effectiveViews.includes('tydenne') && (
                                    <ToggleButton value="tydenne" disabled={!historieTydenne || historieTydenne.length === 0}>
                                        Týdny
                                    </ToggleButton>
                                )}
                                {effectiveViews.includes('histogram') && (
                                    <ToggleButton value="histogram" disabled={!histogramReakce || histogramReakce.length === 0}>
                                        Histogram
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </Grid>
                    )}
                    {(effectiveViews.includes('dny') || effectiveViews.includes('hodiny')) && (
                        <Grid>
                            <ToggleButtonGroup
                                value={view}
                                exclusive
                                onChange={handleViewChange}
                                size="small"
                            >
                                {effectiveViews.includes('dny') && (
                                    <ToggleButton value="dny" disabled={!aktivitaPodleDne || aktivitaPodleDne.length === 0}>
                                        Po dni
                                    </ToggleButton>
                                )}
                                {effectiveViews.includes('hodiny') && (
                                    <ToggleButton value="hodiny" disabled={!aktivitaPodleHodiny || aktivitaPodleHodiny.length === 0}>
                                        Po hod.
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </Grid>
                    )}
                    {(effectiveViews.includes('reakce') || effectiveViews.includes('ucast') || effectiveViews.includes('kumulativni') || effectiveViews.includes('odpovedi')) && (
                        <Grid>
                            <ToggleButtonGroup
                                value={view}
                                exclusive
                                onChange={handleViewChange}
                                size="small"
                            >
                                {effectiveViews.includes('reakce') && (
                                    <ToggleButton value="reakce" disabled={!porovnaniReakce || porovnaniReakce.length === 0}>
                                        Reakce
                                    </ToggleButton>
                                )}
                                {effectiveViews.includes('ucast') && (
                                    <ToggleButton value="ucast" disabled={!trendUcasti || trendUcasti.length === 0}>
                                        Účast
                                    </ToggleButton>
                                )}
                                {effectiveViews.includes('kumulativni') && (
                                    <ToggleButton value="kumulativni" disabled={!kumulativniOdpovedi || kumulativniOdpovedi.length === 0}>
                                        Kumul.
                                    </ToggleButton>
                                )}
                                {effectiveViews.includes('odpovedi') && (
                                    <ToggleButton value="odpovedi">
                                        Souhrn
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </Grid>
                    )}
                </Grid>
            </Box>

            {noData && view !== 'odpovedi' ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height={300}
                    sx={{ opacity: 0.5 }}
                >
                    <Typography color="text.secondary">
                        Žádná data k zobrazení
                    </Typography>
                </Box>
            ) : (
                <Box height={300}>
                    {/* Daily trend chart */}
                    {view === 'trend' && dailyData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={dailyData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="datum"
                                    fontSize={10}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="pocetPoplachu"
                                    name="Poplachy"
                                    stroke={theme.palette.primary.main}
                                    strokeWidth={2}
                                    dot={{ r: 2 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="pocetJedu"
                                    name="JEDU"
                                    stroke={theme.palette.success.main}
                                    strokeWidth={2}
                                    dot={{ r: 2 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="pocetNejedu"
                                    name="NEJEDU"
                                    stroke={theme.palette.error.main}
                                    strokeWidth={2}
                                    dot={{ r: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}

                    {/* Weekly bar chart */}
                    {view === 'tydenne' && weeklyData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={weeklyData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="datum"
                                    fontSize={10}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="pocetJedu"
                                    name="JEDU"
                                    fill={theme.palette.success.main}
                                    stackId="odpovedi"
                                />
                                <Bar
                                    dataKey="pocetNejedu"
                                    name="NEJEDU"
                                    fill={theme.palette.error.main}
                                    stackId="odpovedi"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {/* Histogram of reaction times */}
                    {view === 'histogram' && histogramData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={histogramData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="bucket"
                                    fontSize={11}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    label={{
                                        value: 'Počet',
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fill: theme.palette.text.secondary, fontSize: 12 },
                                    }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Bar
                                    dataKey="pocet"
                                    name="Počet odpovědí"
                                    fill={theme.palette.primary.main}
                                    radius={[4, 4, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {/* Day of week chart */}
                    {view === 'dny' && dayOfWeekData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dayOfWeekData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="den"
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="pocetJedu"
                                    name="JEDU"
                                    fill={theme.palette.success.main}
                                    stackId="odpovedi"
                                />
                                <Bar
                                    dataKey="pocetNejedu"
                                    name="NEJEDU"
                                    fill={theme.palette.error.main}
                                    stackId="odpovedi"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {/* Hour of day chart */}
                    {view === 'hodiny' && hourOfDayData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={hourOfDayData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="hodina"
                                    fontSize={9}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    interval={1}
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="pocetJedu"
                                    name="JEDU"
                                    fill={theme.palette.success.main}
                                    stackId="odpovedi"
                                />
                                <Bar
                                    dataKey="pocetNejedu"
                                    name="NEJEDU"
                                    fill={theme.palette.error.main}
                                    stackId="odpovedi"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {/* Reaction time comparison chart */}
                    {view === 'reakce' && reactionData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={reactionData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="datum"
                                    fontSize={10}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    label={{
                                        value: 'sekundy',
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fill: theme.palette.text.secondary, fontSize: 12 },
                                    }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                    formatter={(value) =>
                                        value !== null && value !== undefined ? `${value} s` : '-'
                                    }
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="uzivatel"
                                    name="Moje reakce"
                                    stroke={theme.palette.primary.main}
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    connectNulls
                                />
                                <Line
                                    type="monotone"
                                    dataKey="jednotka"
                                    name="Průměr jednotky"
                                    stroke={theme.palette.secondary.main}
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={{ r: 2 }}
                                    connectNulls
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}

                    {/* Participation trend chart */}
                    {view === 'ucast' && participationData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={participationData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="datum"
                                    fontSize={10}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    domain={[0, 100]}
                                    label={{
                                        value: '%',
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fill: theme.palette.text.secondary, fontSize: 12 },
                                    }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                    formatter={(value, name) => {
                                        if (name === 'Účast') return value !== null ? `${value}%` : '-';
                                        return value;
                                    }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="procentoUcasti"
                                    name="Účast"
                                    stroke={theme.palette.info.main}
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    connectNulls
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}

                    {/* Cumulative responses area chart */}
                    {view === 'kumulativni' && cumulativeData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={cumulativeData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="datum"
                                    fontSize={10}
                                    tick={{ fill: theme.palette.text.secondary }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="jedu"
                                    name="JEDU"
                                    stackId="1"
                                    stroke={theme.palette.success.main}
                                    fill={theme.palette.success.light}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="nejedu"
                                    name="NEJEDU"
                                    stackId="1"
                                    stroke={theme.palette.error.main}
                                    fill={theme.palette.error.light}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}

                    {/* Pie chart for total responses */}
                    {view === 'odpovedi' && (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={odpovediData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent }) =>
                                        `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                                    }
                                >
                                    {odpovediData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </Box>
            )}
        </Paper>
    );
};

function formatDateLabel(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length >= 3) {
        return `${parseInt(parts[2])}.${parseInt(parts[1])}.`;
    }
    return dateStr;
}

export default React.memo(UserTrendChart);
